/**
 * Ryedical — Animations, Interactions & Scroll Behaviour
 * v4 — March 2026
 */
(function () {
  'use strict';

  /* ---- Respect reduced-motion preference ---- */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Which element actually scrolls ----
     At 990px and up Savor makes .page-wrapper the scroll container (the
     window doesn't move), so scrolling the window did nothing on desktop.
     Fall back to the window when .page-wrapper isn't scrolling (phones). */
  function getScroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight &&
        /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY)) {
      return wrapper;
    }
    return null;
  }

  /* ---- Shared anchor-scroll helper (80px offset clears the sticky header) ---- */
  function scrollToAnchor(id, offset) {
    if (offset == null) offset = 80;
    var el = document.getElementById(id);
    if (!el) return;
    var behavior = prefersReducedMotion ? 'auto' : 'smooth';
    var scroller = getScroller();
    if (scroller) {
      var inner = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - offset;
      scroller.scrollTo({ top: inner, behavior: behavior });
      return;
    }
    var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: top, behavior: behavior });
  }

  /* ==========================================================================
     Scroll Reveal — data-animate system (v4)
     ========================================================================== */
  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach(function (el) { observer.observe(el); });

    // Stagger children with data-stagger on parent
    document.querySelectorAll('[data-stagger]').forEach(function (parent) {
      var delay = parseInt(parent.dataset.stagger) || 80;
      parent.querySelectorAll('[data-animate]').forEach(function (child, i) {
        child.style.transitionDelay = (i * delay) + 'ms';
      });
    });
  }

  /* ==========================================================================
     Smooth Scroll for Anchor Links
     ========================================================================== */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      // A page's own handler (Rye Effect, product page) got there first.
      if (e.defaultPrevented) return;
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (href === '#') return;
      var id = href.slice(1).split('?')[0];
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      scrollToAnchor(id, 80);
    });
  }
  /* ==========================================================================
     FAQ Accordion (v4 classes: .ryedical-faq__question / .ryedical-faq__item)
     ========================================================================== */
  function initFAQ() {
    var questions = document.querySelectorAll('.ryedical-faq__question');
    if (!questions.length) return;

    questions.forEach(function (question, index) {
      question.addEventListener('click', function () {
        var item = this.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        var isOpen = item.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.ryedical-faq__item.is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          var openQuestion = openItem.querySelector('.ryedical-faq__question');
          if (openQuestion) openQuestion.setAttribute('aria-expanded', 'false');
          var a = openItem.querySelector('.ryedical-faq__answer');
          if (a) a.style.maxHeight = '0';
        });

        // Open clicked if it was closed
        if (!isOpen) {
          item.classList.add('is-open');
          this.setAttribute('aria-expanded', 'true');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });

      // Open one useful answer without turning the page into a wall of text.
      if (index === 0) {
        var item = question.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        item.classList.add('is-open');
        question.setAttribute('aria-expanded', 'true');
        if (answer) {
          // Use setTimeout to allow layout to settle
          setTimeout(function () {
            answer.style.maxHeight = answer.scrollHeight + 'px';
          }, 50);
        }
      }
    });

    /* Audit (FAQ clipping fix) — recompute max-height for every open
       answer whenever fonts finish loading or the viewport resizes.
       Without this, the panels are measured before Lora is ready and
       the bottom of long answers gets clipped. */
    function recomputeOpenAnswers() {
      document.querySelectorAll('.ryedical-faq__item.is-open .ryedical-faq__answer').forEach(function (a) {
        a.style.maxHeight = a.scrollHeight + 'px';
      });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recomputeOpenAnswers);
    }
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recomputeOpenAnswers, 120);
    });
  }

  /* ==========================================================================
     Initialise All
     ========================================================================== */
  /* Fix 10 — Mirror aria-hidden onto `inert` so focusable descendants
     inside hidden modals/drawers (search modal, cart drawer when closed)
     stop being tab-reachable. Resolves the Lighthouse a11y check
     "[aria-hidden='true'] elements contain focusable descendants" without
     touching the underlying Taste web components. */
  function initAriaHiddenInertShim() {
    if (!('MutationObserver' in window)) return;

    function applyInert(el) {
      var hidden = el.getAttribute('aria-hidden') === 'true';
      if (hidden) {
        if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
      } else {
        if (el.hasAttribute('inert')) el.removeAttribute('inert');
      }
    }

    /* Initial pass over any element already carrying aria-hidden */
    document.querySelectorAll('[aria-hidden="true"]').forEach(applyInert);

    /* Watch for future attribute changes on aria-hidden, plus newly-added
       elements that arrive with aria-hidden already set. */
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
          applyInert(m.target);
        } else if (m.type === 'childList') {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.hasAttribute && node.hasAttribute('aria-hidden')) applyInert(node);
            if (node.querySelectorAll) {
              node.querySelectorAll('[aria-hidden="true"]').forEach(applyInert);
            }
          });
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
      childList: true
    });
  }

  function init() {
    initScrollReveal();
    initSmoothScroll();
    initFAQ();
    initAriaHiddenInertShim();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
